import { FlagContext } from '@ecs-na/access-flag-react-adapter';
import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { variableCheckPromise } from '../helpers';
import { flagsFactory } from './flags-factory';
import { resolveFlagUser } from './user-resolver';

interface FeatureFlagRootProps {
  children: ReactNode;
}

// Longest we wait for Ensighten to populate `window.ECS.tms.data.ecsstaticid`
// before initialising flags anyway. Bounded so a blocked/absent tag manager can
// never hang flag readiness (and, by extension, any page gated on useFlagReady).
const ECS_STATIC_ID_TIMEOUT_MS = 3000;

/**
 * App-wide feature-flag provider, mounted once per site via `wrapRootElement` in
 * both gatsby-browser and gatsby-ssr so the provider exists during SSR and
 * client hydration alike.
 *
 * This deliberately re-implements the thin adapter `<FlagProvider>` so it can
 * gate `flags.init(...)` on tag-manager readiness. The adapter's provider calls
 * `userResolver()` synchronously the instant it mounts — which is before
 * Ensighten has populated `window.ECS.tms.data`, so `resolveFlagUser` read an
 * empty `ecsstaticid` and the assignment `subject`/`subjectType` came out wrong
 * (a throwaway id, mislabelled). Here we:
 *   - create the flag instance exactly once (so the Datadog OpenFeature
 *     singleton is bound exactly once — remounting/`key` would double-bind);
 *   - keep `FlagContext` mounted the whole time (flag hooks never throw and the
 *     app subtree never remounts);
 *   - only call `init` once `ecsstaticid` is present (or the bounded timeout
 *     elapses).
 *
 * Until init resolves, flags read their defaults and `useFlagReady()` is false —
 * the same progressive-enhancement contract as before. `children` is always
 * rendered (SEO-safe, no hydration mismatch).
 */
export function FeatureFlagRoot({ children }: FeatureFlagRootProps) {
  // Create the flag instance exactly once. Constructing here (never at import
  // time) keeps window/global access out of module scope so SSR import is safe.
  const [flags] = useState(() => flagsFactory());
  const [tmsReady, setTmsReady] = useState(false);
  // Flag-service readiness. The flag hooks re-read getBoolean (and thus emit the
  // Eppo assignment) *primarily* via their own useSyncExternalStore subscription
  // to flags.subscribe, which the service fires on init (doInit -> notify). We
  // additionally track `ready` and key the context value on it as a secondary
  // re-render path: it guarantees consumers re-read even under a
  // notify-before-subscribe ordering, and mirrors the adapter's own FlagProvider.
  // It is a safety net, not the sole trigger.
  const [ready, setReady] = useState(false);
  const initStarted = useRef(false);

  // 1. Wait for the tag manager to populate ecsstaticid (bounded). `|| undefined`
  //    keeps us waiting through an empty-string placeholder for the real id
  //    instead of resolving early on ''.
  useEffect(() => {
    let alive = true;
    variableCheckPromise(
      () => window?.ECS?.tms?.data?.ecsstaticid || undefined,
      ECS_STATIC_ID_TIMEOUT_MS
    ).finally(() => {
      if (alive) {
        setTmsReady(true);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  // 2. Initialise flags exactly once, only after tms data is ready, so
  //    resolveFlagUser sees the real ecsstaticid. Tracks readiness like the
  //    adapter's useFlagService (subscribe + init); see the `ready` note above
  //    for why this is a secondary re-render path rather than the assignment's
  //    sole trigger.
  useEffect(() => {
    if (!tmsReady) {
      return;
    }
    let alive = true;
    if (flags.isReady()) {
      setReady(true);
      return;
    }
    const unsubscribe = flags.subscribe(() => {
      if (alive) {
        setReady(flags.isReady());
      }
    });
    if (!initStarted.current) {
      initStarted.current = true;
      flags
        .init(resolveFlagUser())
        .catch(() => {
          // Flag init failure must never break the app; hooks fall back to defaults.
        })
        .finally(() => {
          if (alive) {
            setReady(true);
          }
        });
    }
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [tmsReady, flags]);

  // Secondary re-render path: a new object when readiness changes ensures
  // consumers re-read their flags after init even if they subscribed after the
  // service's init notification (see the `ready` note above).
  const value = useMemo(() => ({ flags }), [flags, ready]);

  return <FlagContext.Provider value={value}>{children}</FlagContext.Provider>;
}
